import { useList, useOne } from "@refinedev/core";
import { useAssetById } from "hooks/useAssetById/";
import {
  ContactResponse,
  MediaProjectResponse,
  PresenterProfileResponse,
  SlidesAsset,
  StoryboardAsset,
} from "pages/media/types";
import { isProjectFlash } from "pages/media/projects/helpers/helper";

export const useProjectAssets = ({
  projectId,
  instanceId,
}: {
  projectId?: string;
  instanceId?: string;
}) => {
  const {
    data: projectData,
    isLoading: isProjectLoading,
    isRefetching: isProjectRefetching,
    isFetching: isProjectFetching,
  } = useOne<MediaProjectResponse>({
    resource: `media/projects`,
    id: projectId,
    queryOptions: {
      enabled: !!projectId,
    },
  });

  const isProjectDataLoading = projectId
    ? isProjectLoading || isProjectRefetching || isProjectFetching
    : false;

  const organizationId = projectData?.data.organization_id;
  const brandKitId = projectData?.data.settings.brand_kit_id;

  const media = projectData?.data.media.find(
    (m) => m.instance_id === instanceId
  );

  const storyboard = projectData?.data.storyboards.find(
    (sb) => sb.id === media?.storyboard_settings_id
  );
  const storyboardId = storyboard?.asset_id;
  const storyboardLanguage = storyboard?.language;
  const projectMedias = projectData?.data.media;
  const primaryDocumentId = projectData?.data.settings.primary_document_id;

  const { data: storyboardAsset } = useAssetById<StoryboardAsset>({
    assetId: storyboardId,
  });

  const storyboardPath = storyboardAsset?.data.path;

  const slidesAssetId = storyboard?.slides_asset_id;

  const { data: slidesAsset } = useAssetById<SlidesAsset>({
    assetId: slidesAssetId,
  });

  const slidesPath = slidesAsset?.data.path;

  const {
    data: contactData,
    isLoading: isContactLoading,
    isRefetching: isContactRefetching,
    isFetching: isContactFetching,
  } = useOne<ContactResponse>({
    resource: `media/${organizationId}/contacts`,
    id: projectData?.data.settings.contact_id ?? undefined,
    queryOptions: {
      enabled: !!projectData?.data.settings.contact_id && !!organizationId,
    },
  });
  const isContactDataLoading = projectData?.data.settings.contact_id
    ? isContactLoading || isContactRefetching || isContactFetching
    : false;

  const {
    data: initialPresenterData,
    isLoading: isInitialPresenterLoading,
    isRefetching: isInitialPresenterRefetching,
    isFetching: isInitialPresenterFetching,
  } = useOne<PresenterProfileResponse>({
    resource: `media/${organizationId}/presenter_profiles`,
    id: contactData?.data.presenter_id,
    queryOptions: {
      enabled: !!contactData?.data.presenter_id && !!organizationId,
    },
  });
  const isInitialPresenterDataLoading = contactData?.data.presenter_id
    ? isInitialPresenterLoading ||
      isInitialPresenterRefetching ||
      isInitialPresenterFetching
    : false;

  const { data: projectPresenters, isLoading: isProjectPresentersLoading } =
    useList<PresenterProfileResponse>({
      resource: `media/${organizationId}/presenter_profiles`,
      queryOptions: {
        enabled: !!organizationId,
      },
    });

  const hasTransition =
    projectData?.data.settings.transitions !== "NoTransition";

  // Gate on the presenter-profiles list too: chapter avatars are resolved from
  // this list by id, and the editor store is created only once — mapping before
  // it loads would permanently fall back to the project-level presenter.
  const isDataLoading =
    isProjectDataLoading ||
    isContactDataLoading ||
    isInitialPresenterDataLoading ||
    (!!organizationId && isProjectPresentersLoading);

  return {
    contact: contactData?.data,
    isDataLoading,
    projectTitle: projectData?.data.title,
    organizationId,
    brandKitId,
    presenter: initialPresenterData?.data,
    storyboardId,
    storyboard,
    storyboardSettingsId: storyboard?.id,
    storyboardPath,
    slidesPath,
    hasTransition,
    projectPresenters: projectPresenters?.data,
    projectMedias,
    primaryDocumentId,
    storyboardLanguage,
    hasMedia: !!media?.id,
    projectMedia: media,
    themeId: media?.theme_id ?? undefined,
    isFlashProject: isProjectFlash(projectData?.data.type ?? ""),
    projectType: projectData?.data.type,
    isPromptProject:
      projectData?.data.documents[0]?.retrieval?.provider.type ===
      "DocumentGeneratorProvider",
  };
};
